import { useApiClient } from '@/api/ApiClientProvider'
import type { ApiClientKind } from '@/api/client'
import type { CustomerRow, CustomersList, CustomersQuery } from '@/api/customers'
import { filtersKey } from '@/lib/useGlobalFilters'

import type { FoldedList } from '../list/paging'
import { useServerList } from '../list/useServerList'

/*
 * The list's requests: its pages, cached as every list's are. The two requests that
 * identify a person, the search and the reveal, are never cached and live elsewhere
 * (`customerSearch.ts`, `customerReveal.ts`).
 */

export type CustomersResult = FoldedList<CustomerRow, CustomersList>

/**
 * A customer's identity across pages: the synced record's `customer_id`. An unsynced
 * customer has none, so it is told apart by what the API serves about it: its website,
 * environment, masked reference and first purchase to the second. Two unsynced customers
 * alike in all four would be shown once, which no real pair of first orders is.
 */
export const identify = (row: CustomerRow): string =>
  row.customerId ?? `unsynced:${row.websiteId}:${row.environment}:${row.customerRef}:${row.firstPurchaseAt ?? ''}`

/** Keyed by the query less its page: `useInfiniteQuery` keeps the pages within one entry. */
export const customersKeys = {
  all: ['analytics-customers'] as const,
  list: (kind: ApiClientKind, query: CustomersQuery) =>
    [
      'analytics-customers',
      kind,
      filtersKey(query.filters),
      query.basis ?? null,
      query.customerType ?? null,
      query.member ?? null,
      query.vip ?? null,
      query.consent ?? [],
      query.sort ?? null,
      query.order ?? null,
    ] as const,
}

/** The list's pages, folded (`../list/useServerList`). */
export function useCustomersList(query: CustomersQuery) {
  const client = useApiClient()
  return useServerList<CustomerRow, CustomersList>({
    queryKey: customersKeys.list(client.kind, query),
    fetchPage: (page) => client.customers({ ...query, ...page }),
    identify,
  })
}
